import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, Icon, Text, VStack} from 'platform/foundation';

import {DeepPartial} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useSendCustomerSmsMutation} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {getCustomerPhoneNumber} from '../../utils/getCustomerPhoneNumber';
import {handleApiError} from '../../utils/handleApiError';
import {CustomerSelectField} from './components/CustomerSelectField';
import {MessageFormField} from './components/MessageFormField';
import {SmsMessagePreview} from './components/SmsMessagePreview';
import {useGetStructuredPhoneNumber} from './hooks/useGetStructuredPhoneNumber';
import {FormValues, SendCustomerSmsFormSchema} from './types/FormValues';

export interface SentSmsToCustomerFormProps extends TestIdProps {
  // SHOULD be always present, but form can function without them as "general send sms action"
  customerId?: string | Nullish;
  recordId?: string | Nullish;
  resourceId?: EntityResourceIds | Nullish;
}

export function SendCustomerSmsForm(props: SentSmsToCustomerFormProps) {
  const {
    data: customer,
    isLoading: isLoadingCustomer,
    isError: isCustomerError,
  } = useGetCustomerV2Query(
    {
      customerId: props.customerId ?? '',
    },
    {skip: isNilOrEmpty(props.customerId)}
  );

  const getStructuredPhoneNumber = useGetStructuredPhoneNumber();
  const [sendCustomerSms] = useSendCustomerSmsMutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (data) => {
    const phoneNumber = getStructuredPhoneNumber(data.phoneNumber);

    await sendCustomerSms({
      sendCustomerSmsRequestBody: {
        body: data.body ?? '',
        customerId: data.customerId,
        resourceId: props.resourceId,
        recordId: props.recordId,
        customerPhoneNumber: phoneNumber,
      },
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.customer.labels.smsSuccessfullySend')))
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const isLoading = isLoadingCustomer;
  const isError = isCustomerError;

  const defaultValues: DeepPartial<FormValues> = {
    customerId: props.customerId ?? undefined,
    phoneNumber: getCustomerPhoneNumber(customer) ?? undefined,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<FormValues>
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        experimentalZodSchema={SendCustomerSmsFormSchema}
      >
        {(control, formApi) => (
          <Grid columns={[1, 1, 2]}>
            <VStack spacing={2} minWidth={0}>
              <Box paddingHorizontal={[4, 4, 10]}>
                <VStack spacing={4} align="center" justify="center">
                  <Icon
                    value="action/send_to_mobile"
                    color="palettes.blue.30.100"
                    isNotFilled
                    size={20}
                    data-testid={suffixTestId('messageIcon', props)}
                  />

                  <Heading size={3}>
                    {i18n.t('entity.customer.labels.customer_message_title_sms')}
                  </Heading>
                  <Text color="secondary" align="center">
                    {i18n.t('entity.customer.labels.customer_message_title_description_sms')}
                  </Text>
                </VStack>
              </Box>

              <CustomerSelectField
                control={control}
                formApi={formApi}
                data-testid={suffixTestId('customerSelectField', props)}
              />

              <MessageFormField
                control={control}
                formApi={formApi}
                resourceId={props.resourceId}
                recordId={props.recordId}
                data-testid={suffixTestId('messageFormField', props)}
              />
            </VStack>

            <SmsMessagePreview
              control={control}
              data-testid={suffixTestId('smsMessagePreview', props)}
            />

            <GridItem span={[1, 1, 2]}>
              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  title={i18n.t('general.actions.cancel')}
                  onClick={closeCurrentDialog}
                  data-testid={suffixTestId('cancelButton', props)}
                />
                <FormButton
                  type="submit"
                  control={control}
                  title={i18n.t('general.actions.send')}
                  data-testid={suffixTestId('sendButton', props)}
                />
              </ButtonGroup>
            </GridItem>
          </Grid>
        )}
      </Form>
    </DataStatus>
  );
}
